import React, { useState, useEffect, useRef } from 'react';
import * as api from '../../../api/contentStudioV2';
import { useLanguage } from '../../../contexts/LanguageContext';
import { 
  validateKnowledgeForm, 
  getErrorMessage, 
  scrollToFirstError 
} from '../utils/formValidation';
import '../styles/formErrors.css';
import { txt } from '../../../utils/bilingualText';

const Step3KnowledgeSelection = ({ sessionId, token, sessionData, onComplete, onBack, setError, setLoading }) => {
  const { t } = useLanguage();
  const [packs, setPacks] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(() => {
    if (sessionData && sessionData.selectedProducts) {
      console.log('[Step3] Restoring selectedProducts:', sessionData.selectedProducts);
      return sessionData.selectedProducts;
    }
    return [];
  });
  const [selectedServices, setSelectedServices] = useState(() => {
    if (sessionData && sessionData.selectedServices) {
      return sessionData.selectedServices;
    }
    return [];
  });
  const [selectedValues, setSelectedValues] = useState(() => {
    if (sessionData && sessionData.selectedValues) {
      return sessionData.selectedValues;
    }
    return [];
  });
  const [selectedDirection, setSelectedDirection] = useState(() => {
    if (sessionData && sessionData.selectedDirection) {
      return sessionData.selectedDirection;
    }
    return [];
  });
  const [selectedAmbassador, setSelectedAmbassador] = useState(() => {
    if (sessionData && sessionData.selectedAmbassador) {
      return sessionData.selectedAmbassador;
    }
    return [];
  });
  
  const [knowledgeSpider, setKnowledgeSpider] = useState(() => {
    if (sessionData && sessionData.knowledgeSpider) {
      return sessionData.knowledgeSpider;
    }
    return null;
  });
  const [errors, setErrors] = useState({});
  const [locale, setLocale] = useState('en');
  const [loadingPacks, setLoadingPacks] = useState(true);
  const [expandedDescs, setExpandedDescs] = useState({});

  const refs = {
    productPacks: useRef(null),
    brandingPacks: useRef(null)
  };

  const errorOrder = ['productPacks', 'brandingPacks'];
  
  const objective = sessionData?.objective || 'Sales';
  const isSales = objective === 'Sales';
  const isBranding = objective === 'Branding' || objective === 'Awareness';

  useEffect(() => {
    loadPacks();
  }, []);

  const loadPacks = async () => {
    setLoadingPacks(true);
    try {
      console.log('[Step3] Loading packs for objective:', objective);
      
      let packTypes = '';
      if (isSales) {
        packTypes = 'product,services';  // Fixed: 'services' plural to match PackType enum
      } else if (isBranding) {
        packTypes = 'values,direction,ambassador';
      }
      
      const result = await api.getKnowledgePacksByTypes(token, packTypes);
      console.log('[Step3] Packs loaded:', result);
      
      result.packs?.forEach(pack => {
        if (!pack.title && !pack.name && !pack.name_en) {
          console.warn('[Step3] Pack missing title:', pack.pack_id);
        }
      });
      
      setPacks(result.packs || []);
      setError(null);
    } catch (err) {
      console.error('[Step3] Failed to load packs:', err);
      
      if (err.message.includes('401') || err.message.includes('Unauthorized') || err.message.includes('authentication')) {
        setError(getErrorMessage('sessionExpired', locale));
      } else {
        setError('Failed to load Knowledge Packs: ' + err.message);
      }
    } finally {
      setLoadingPacks(false);
    }
  };

  const retryLoadPacks = () => {
    setError(null);
    loadPacks();
  };

  const togglePack = (packId, packType) => {
    switch(packType) {
      case 'product':
        setSelectedProducts(prev => 
          prev.includes(packId) ? prev.filter(id => id !== packId) : [...prev, packId]
        );
        if (errors.productPacks) setErrors(prev => ({ ...prev, productPacks: null }));
        break;
      case 'service':
        setSelectedServices(prev => 
          prev.includes(packId) ? prev.filter(id => id !== packId) : [...prev, packId]
        );
        if (errors.productPacks) setErrors(prev => ({ ...prev, productPacks: null }));
        break;
      case 'values':
        setSelectedValues(prev => 
          prev.includes(packId) ? prev.filter(id => id !== packId) : [...prev, packId]
        );
        if (errors.brandingPacks) setErrors(prev => ({ ...prev, brandingPacks: null }));
        break;
      case 'direction':
        setSelectedDirection(prev => 
          prev.includes(packId) ? prev.filter(id => id !== packId) : [...prev, packId]
        );
        if (errors.brandingPacks) setErrors(prev => ({ ...prev, brandingPacks: null }));
        break;
      case 'ambassador':
        setSelectedAmbassador(prev => 
          prev.includes(packId) ? prev.filter(id => id !== packId) : [...prev, packId]
        );
        if (errors.brandingPacks) setErrors(prev => ({ ...prev, brandingPacks: null }));
        break;
    }
  };

  const handleContinue = async () => {
    const validationErrors = validateKnowledgeForm(
      { selectedProducts, selectedServices, selectedValues, selectedDirection, selectedAmbassador },
      objective
    );
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      scrollToFirstError({ 
        errorOrder, 
        refs, 
        errors: validationErrors, 
        headerOffset: 92 
      });
      const errorKey = Object.values(validationErrors)[0];
      setError(getErrorMessage(errorKey, locale));
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const selections = {
        products: selectedProducts,
        services: selectedServices,
        values: selectedValues,
        direction: selectedDirection,
        ambassador: selectedAmbassador
      };
      
      const result = await api.selectKnowledge(token, sessionId, objective, selections);
      setKnowledgeSpider(result.knowledge_spider);
      onComplete({ 
        knowledgeSpider: result.knowledge_spider, 
        contextHash: result.context_hash,
        selectedProducts,
        selectedServices,
        selectedValues,
        selectedDirection,
        selectedAmbassador
      });
    } catch (err) {
      if (err.message.includes('401') || err.message.includes('authentication')) {
        setError(getErrorMessage('sessionExpired', locale));
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getPackTitle = (pack) => {
    return txt(pack.title, 'en') || txt(pack.name, 'en') || pack.name_en || 'Untitled Pack';
  };

  const getPackSubtitle = (pack) => {
    return txt(pack.subtitle, 'en') || txt(pack.description, 'en') || null;
  };

  const getPackTags = (pack) => {
    let tags = [];
    
    if (pack.metadata && pack.metadata.tags && Array.isArray(pack.metadata.tags)) {
      tags = pack.metadata.tags;
    } else if (pack.tags && Array.isArray(pack.tags)) {
      tags = pack.tags;
    }
    
    return tags.slice(0, 3);
  };
  
  const isSelected = (packId, packType) => {
    switch(packType) {
      case 'product': return selectedProducts.includes(packId);
      case 'service': return selectedServices.includes(packId);
      case 'values': return selectedValues.includes(packId);
      case 'direction': return selectedDirection.includes(packId);
      case 'ambassador': return selectedAmbassador.includes(packId);
      default: return false;
    }
  };
  
  const renderPackCard = (pack) => {
    const title = getPackTitle(pack);
    const subtitle = getPackSubtitle(pack);
    const tags = getPackTags(pack);
    const packType = pack.pack_type;
    const selected = isSelected(pack.pack_id, packType);
    
    return (
      <div
        key={pack.pack_id}
        onClick={() => togglePack(pack.pack_id, packType)}
        tabIndex={0}
        onKeyPress={(e) => e.key === 'Enter' && togglePack(pack.pack_id, packType)}
        style={{
          padding: '14px',
          border: selected ? '2px solid #0e67a2' : '1px solid #ddd',
          borderRadius: '10px',
          background: selected ? '#E8F4FF' : '#fff',
          cursor: 'pointer',
          transition: 'all 0.2s',
          boxShadow: selected ? '0 2px 8px rgba(0, 122, 255, 0.1)' : 'none'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: 600, 
              color: selected ? '#0e67a2' : '#333',
              marginBottom: '4px'
            }}>
              {title}
            </div>
            
            {subtitle && (
              <div style={{ 
                fontSize: '13px', 
                color: '#666', 
                marginBottom: '8px',
                lineHeight: '1.4'
              }}>
                {subtitle.length > 90 && !expandedDescs[pack.pack_id] ? (
                  <>
                    {subtitle.slice(0, 90)}...{' '}
                    <span
                      onClick={(e) => { e.stopPropagation(); setExpandedDescs(prev => ({ ...prev, [pack.pack_id]: true })); }}
                      style={{ color: '#0e67a2', cursor: 'pointer', fontWeight: 500 }}
                    >
                      See more
                    </span>
                  </>
                ) : (
                  <>
                    {subtitle}
                    {subtitle.length > 90 && (
                      <>
                        {' '}
                        <span
                          onClick={(e) => { e.stopPropagation(); setExpandedDescs(prev => ({ ...prev, [pack.pack_id]: false })); }}
                          style={{ color: '#0e67a2', cursor: 'pointer', fontWeight: 500 }}
                        >
                          See less
                        </span>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
            
            {tags.length > 0 && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                <span style={{
                  padding: '3px 8px',
                  background: selected ? '#0e67a2' : '#F0F0F0',
                  color: selected ? '#fff' : '#666',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase'
                }}>
                  {packType}
                </span>
                {tags.map((tag, idx) => (
                  <span
                    key={`item-${idx}`}
                    style={{
                      padding: '3px 8px',
                      background: selected ? 'rgba(0, 122, 255, 0.1)' : '#F0F0F0',
                      color: selected ? '#0e67a2' : '#666',
                      borderRadius: '4px',
                      fontSize: '11px'
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
          
          <div style={{ 
            width: '28px', 
            height: '28px', 
            borderRadius: '6px',
            border: selected ? '2px solid #0e67a2' : '2px solid #ddd',
            background: selected ? '#0e67a2' : '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.2s'
          }}>
            {selected && (
              <span style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>✓</span>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  const helperText = isSales 
    ? (locale === 'en' 
        ? "For Sales objective, choose at least one Product or Service." 
        : "Untuk objektif Sales, pilih sekurang-kurangnya satu Produk atau Servis.")
    : (locale === 'en'
        ? "For Branding/Awareness, choose at least one: Company Values, Campaign Direction, or Ambassador."
        : "Untuk Branding/Awareness, pilih sekurang-kurangnya satu: Company Values, Campaign Direction, atau Ambassador.");

  return (
    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px' }}>
      <h2 style={{ fontSize: '20px', color: '#333', marginBottom: '8px' }}>
        {t('cs.steps.knowledge.title')}
      </h2>
      <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
        {helperText}
      </p>
      
      {Object.keys(errors).length > 0 && (
        <div className="error-summary">
          {getErrorMessage('generalError', locale)}
        </div>
      )}
      
      {loadingPacks && (
        <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
          {t('cs.steps.knowledge.loading')}
        </div>
      )}

      {!loadingPacks && packs.length === 0 && (
        <div 
          ref={refs.productPacks}
          style={{ 
            padding: '24px', 
            textAlign: 'center', 
            color: '#999',
            border: errors.productPacks || errors.brandingPacks ? '2px solid #E53935' : '1px solid #ddd',
            borderRadius: '8px',
            background: errors.productPacks || errors.brandingPacks ? '#FFEBEE' : '#f5f5f5'
          }}
        >
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>📦</div>
          <div style={{ marginBottom: '12px', fontSize: '15px', fontWeight: 600, color: '#666' }}>
            {t('cs.steps.knowledge.noPacks')}
          </div>
          <div style={{ marginBottom: '16px', fontSize: '13px', color: '#999' }}>
            {locale === 'bm' 
              ? 'Tiada pack produk tersedia. Sila cipta atau publish Product Pack di Knowledge Library terlebih dahulu.'
              : 'No available product packs found. Please create or publish a Product Pack in Knowledge Library first.'}
          </div>
          <button
            onClick={retryLoadPacks}
            style={{
              padding: '8px 16px',
              background: '#0e67a2',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600
            }}
          >
            {t('cs.steps.knowledge.retry')}
          </button>
        </div>
      )}

      {!loadingPacks && packs.length > 0 && (
        <div ref={refs.productPacks} style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {packs.map(pack => renderPackCard(pack))}
          </div>
          {(errors.productPacks || errors.brandingPacks) && (
            <div className="error-text" id="packs-err" style={{ marginTop: '8px' }}>
              {getErrorMessage(errors.productPacks || errors.brandingPacks, locale)}
            </div>
          )}
        </div>
      )}

      {knowledgeSpider && (
        <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px', marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '8px' }}>🕸️ Knowledge Spider (Auto-fetched)</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Campaigns: {knowledgeSpider.related_campaigns?.length || 0} |
            Promotions: {knowledgeSpider.related_promotions?.length || 0} |
            Ambassadors: {knowledgeSpider.related_ambassadors?.length || 0} |
            Compliance: {knowledgeSpider.compliance_rules?.length || 0}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
        <button 
          onClick={onBack} 
          style={{ 
            flex: 1, 
            padding: '14px', 
            background: '#fff', 
            border: '2px solid #0e67a2', 
            borderRadius: '12px', 
            color: '#0e67a2', 
            fontSize: '15px', 
            fontWeight: 600, 
            cursor: 'pointer' 
          }}
        >
          {t('cs.buttons.back')}
        </button>
        <button 
          onClick={handleContinue}
          disabled={loadingPacks || (packs.length === 0)}
          style={{ 
            flex: 2, 
            padding: '14px', 
            background: (loadingPacks || packs.length === 0) ? '#ccc' : '#F7941E', 
            border: 'none', 
            borderRadius: '12px', 
            color: '#fff', 
            fontSize: '15px', 
            fontWeight: 600, 
            cursor: (loadingPacks || packs.length === 0) ? 'not-allowed' : 'pointer' 
          }}
        >
          {t('cs.buttons.continue')}
        </button>
      </div>
    </div>
  );
};

export default Step3KnowledgeSelection;
